import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { Portfolio, Transaction, Holding, Stock } from '../types';
import { MOCK_STOCKS, simulatePriceUpdate } from '../data/mockStocks';

interface PortfolioState {
  portfolio: Portfolio;
  stocks: Stock[];
  isLoading: boolean;
}

type PortfolioAction =
  | { type: 'INITIALIZE_PORTFOLIO' }
  | { type: 'UPDATE_STOCK_PRICES' }
  | { type: 'BUY_STOCK'; payload: { ticker: string; quantity: number; price: number } }
  | { type: 'SELL_STOCK'; payload: { ticker: string; quantity: number; price: number } }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: PortfolioState = {
  portfolio: {
    cash: 1000000, // $1M virtual funds
    totalValue: 1000000,
    holdings: [],
    transactions: []
  },
  stocks: MOCK_STOCKS,
  isLoading: false
};

const portfolioReducer = (state: PortfolioState, action: PortfolioAction): PortfolioState => {
  switch (action.type) {
    case 'INITIALIZE_PORTFOLIO':
      return {
        ...state,
        portfolio: {
          cash: 1000000,
          totalValue: 1000000,
          holdings: [],
          transactions: []
        }
      };

    case 'UPDATE_STOCK_PRICES':
      const updatedStocks = state.stocks.map(simulatePriceUpdate);
      const updatedHoldings = state.portfolio.holdings.map(holding => {
        const stock = updatedStocks.find(s => s.ticker === holding.ticker);
        if (!stock) return holding;
        
        const totalValue = holding.quantity * stock.currentPrice;
        const unrealizedPL = totalValue - (holding.quantity * holding.averagePrice);
        const unrealizedPLPercent = (unrealizedPL / (holding.quantity * holding.averagePrice)) * 100;
        
        return {
          ...holding,
          currentPrice: stock.currentPrice,
          totalValue,
          unrealizedPL,
          unrealizedPLPercent
        };
      });
      
      const totalValue = updatedHoldings.reduce((sum, h) => sum + h.totalValue, 0) + state.portfolio.cash;
      
      return {
        ...state,
        stocks: updatedStocks,
        portfolio: {
          ...state.portfolio,
          holdings: updatedHoldings,
          totalValue
        }
      };

    case 'BUY_STOCK': {
      const { ticker, quantity, price } = action.payload;
      const totalCost = quantity * price;
      
      if (state.portfolio.cash < totalCost) {
        return state; // Not enough cash
      }
      
      const existingHolding = state.portfolio.holdings.find(h => h.ticker === ticker);
      let updatedHoldings: Holding[];
      
      if (existingHolding) {
        // Update existing holding
        const newQuantity = existingHolding.quantity + quantity;
        const newAveragePrice = ((existingHolding.quantity * existingHolding.averagePrice) + totalCost) / newQuantity;
        
        updatedHoldings = state.portfolio.holdings.map(h =>
          h.ticker === ticker
            ? {
                ...h,
                quantity: newQuantity,
                averagePrice: newAveragePrice,
                totalValue: newQuantity * price,
                unrealizedPL: (newQuantity * price) - (newQuantity * newAveragePrice),
                unrealizedPLPercent: ((newQuantity * price) - (newQuantity * newAveragePrice)) / (newQuantity * newAveragePrice) * 100
              }
            : h
        );
      } else {
        // Create new holding
        const newHolding: Holding = {
          ticker,
          quantity,
          averagePrice: price,
          currentPrice: price,
          totalValue: totalCost,
          unrealizedPL: 0,
          unrealizedPLPercent: 0
        };
        updatedHoldings = [...state.portfolio.holdings, newHolding];
      }
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'buy',
        ticker,
        quantity,
        price,
        total: totalCost,
        timestamp: new Date()
      };
      
      const totalValue = updatedHoldings.reduce((sum, h) => sum + h.totalValue, 0) + (state.portfolio.cash - totalCost);
      
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          cash: state.portfolio.cash - totalCost,
          holdings: updatedHoldings,
          transactions: [...state.portfolio.transactions, newTransaction],
          totalValue
        }
      };
    }

    case 'SELL_STOCK': {
      const { ticker, quantity, price } = action.payload;
      const existingHolding = state.portfolio.holdings.find(h => h.ticker === ticker);
      
      if (!existingHolding || existingHolding.quantity < quantity) {
        return state; // Not enough shares
      }
      
      const totalValue = quantity * price;
      const newQuantity = existingHolding.quantity - quantity;
      
      let updatedHoldings: Holding[];
      if (newQuantity === 0) {
        // Remove holding completely
        updatedHoldings = state.portfolio.holdings.filter(h => h.ticker !== ticker);
      } else {
        // Update holding quantity
        updatedHoldings = state.portfolio.holdings.map(h =>
          h.ticker === ticker
            ? {
                ...h,
                quantity: newQuantity,
                totalValue: newQuantity * price,
                unrealizedPL: (newQuantity * price) - (newQuantity * h.averagePrice),
                unrealizedPLPercent: ((newQuantity * price) - (newQuantity * h.averagePrice)) / (newQuantity * h.averagePrice) * 100
              }
            : h
        );
      }
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'sell',
        ticker,
        quantity,
        price,
        total: totalValue,
        timestamp: new Date()
      };
      
      const portfolioTotalValue = updatedHoldings.reduce((sum, h) => sum + h.totalValue, 0) + (state.portfolio.cash + totalValue);
      
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          cash: state.portfolio.cash + totalValue,
          holdings: updatedHoldings,
          transactions: [...state.portfolio.transactions, newTransaction],
          totalValue: portfolioTotalValue
        }
      };
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };

    default:
      return state;
  }
};

const PortfolioContext = createContext<{
  state: PortfolioState;
  dispatch: React.Dispatch<PortfolioAction>;
} | null>(null);

export const PortfolioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(portfolioReducer, initialState);

  // Update stock prices every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Updating stock prices...');
      dispatch({ type: 'UPDATE_STOCK_PRICES' });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <PortfolioContext.Provider value={{ state, dispatch }}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};
